import { Type } from "@sinclair/typebox";

export const GarminAuthResponseSchema = Type.Object({
	access_token: Type.String(),
	token_type: Type.Optional(Type.String()),
	expires_in: Type.Optional(Type.Number()),
	scope: Type.Optional(Type.String()),
});

export type GarminAuthResponse = typeof GarminAuthResponseSchema.static;

export const GARMIN_LOG_STATUSES = ["CREATED", "UPDATED", "DELETED"] as const;

export const GarminLogStatusSchema = Type.Union(
	GARMIN_LOG_STATUSES.map((s) => Type.Literal(s)),
);

export type GarminLogStatus = (typeof GARMIN_LOG_STATUSES)[number];

/**
 * One entry from the flight-log reference queue. `id` is the queue cursor value and the
 * handle passed to the file endpoint; `flightLogUuid` identifies the log itself across
 * versions. Tail and customer ride on the reference, not on the file.
 */
export const GarminLogReferenceSchema = Type.Object({
	id: Type.String(),
	flightLogUuid: Type.String(),
	version: Type.String(),
	status: GarminLogStatusSchema,
	tailNumber: Type.String(),
	mutualCustomerId: Type.String(),
});

export type GarminLogReference = typeof GarminLogReferenceSchema.static;

export const GarminLogReferencesResponseSchema = Type.Array(GarminLogReferenceSchema);

export const GARMIN_AUTH_NOTIFICATION_STATUSES = [
	"CUSTOMER_AUTHENTICATION_GRANTED",
	"CUSTOMER_AUTHENTICATION_REVOKED",
	"AIRCRAFT_AUTHENTICATION_GRANTED",
	"AIRCRAFT_AUTHENTICATION_REVOKED",
] as const;

export const GarminAuthNotificationStatusSchema = Type.Union(
	GARMIN_AUTH_NOTIFICATION_STATUSES.map((s) => Type.Literal(s)),
);

export type GarminAuthNotificationStatus = (typeof GARMIN_AUTH_NOTIFICATION_STATUSES)[number];

/** The spec says these are present only on AIRCRAFT_* statuses; the live API sends them as
 *  explicit nulls on CUSTOMER_* ones. */
export const GarminAuthNotificationSchema = Type.Object({
	id: Type.String(),
	mutualCustomerIdentifier: Type.String(),
	status: GarminAuthNotificationStatusSchema,
	allowedDataType: Type.Optional(Type.Union([Type.String(), Type.Null()])),
	aircraftTailNumber: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});

export type GarminAuthNotification = typeof GarminAuthNotificationSchema.static;

export const GarminAuthNotificationsResponseSchema = Type.Array(GarminAuthNotificationSchema);

const GARMIN_RETURN_HOSTS = ["garmin.com", "garmin.cn"];

/**
 * `returnUrl` arrives as a query param on a link anyone can craft, so it is only safe to
 * navigate to after checking it lands back on Garmin. Without this the handshake page is an
 * open redirect.
 */
export const isGarminReturnUrl = (value: string): boolean => {
	let url: URL;
	try {
		url = new URL(value);
	} catch {
		return false;
	}
	if (url.protocol !== "https:") {
		return false;
	}
	const host = url.hostname.toLowerCase();
	return GARMIN_RETURN_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
};

/** Garmin mints MCIs as UUIDs; reject anything else before it reaches the database. */
export const isMutualCustomerIdentifier = (value: string): boolean =>
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
